/**
 * Bottom sheet to multi-select users (profiles) for admin notifications.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { UserProfile, accessControlService } from '@watchlist/shared';
import InitialsAvatar from '../widgets/InitialsAvatar';
import { font } from '../../utils/fontHelper';

export interface RecipientPickerBottomSheetProps {
  open: boolean;
  onClose: () => void;
  isDark: boolean;
  initialSelected: UserProfile[];
  onConfirm: (users: UserProfile[]) => void;
}

export const RecipientPickerBottomSheet: React.FC<RecipientPickerBottomSheetProps> = ({
  open,
  onClose,
  isDark,
  initialSelected,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [search, setSearch] = useState('');
  const [selectedById, setSelectedById] = useState<Record<string, UserProfile>>({});

  useEffect(() => {
    if (!open) return;
    setSelectedById(
      initialSelected.reduce<Record<string, UserProfile>>((acc, u) => {
        acc[u.uid] = u;
        return acc;
      }, {})
    );
  }, [open, initialSelected]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await accessControlService.getAllUsersWithRoles(500);
      setUsers(list);
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    void loadUsers();
  }, [open, loadUsers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.displayName?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.username?.toLowerCase().includes(q) ||
        u.uid.toLowerCase().includes(q)
    );
  }, [users, search]);

  const toggleUser = useCallback((user: UserProfile) => {
    setSelectedById((prev) => {
      const next = { ...prev };
      if (next[user.uid]) {
        delete next[user.uid];
      } else {
        next[user.uid] = user;
      }
      return next;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(Object.values(selectedById));
    onClose();
  }, [onConfirm, onClose, selectedById]);

  const selectedCount = Object.keys(selectedById).length;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxHeight: '88vh',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          bgcolor: isDark ? '#1a1a1f' : '#fafafa',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 360 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 18 }}>
            Select recipients
          </Typography>
          <IconButton onClick={onClose} aria-label="Close" size="small">
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, pt: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search name, email, username…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: font() }}>
            {selectedCount} selected · {filtered.length} shown
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={36} />
            </Box>
          )}
          {loadError && (
            <Typography color="error" sx={{ px: 2, py: 2, fontFamily: font() }}>
              {loadError}
            </Typography>
          )}
          {!loading && !loadError && (
            <List dense disablePadding>
              {filtered.map((user) => {
                const checked = Boolean(selectedById[user.uid]);
                const secondary = [user.username ? `@${user.username}` : null, user.email]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <ListItem key={user.uid} disablePadding>
                    <ListItemButton onClick={() => toggleUser(user)} sx={{ borderRadius: 1, gap: 1 }}>
                      <Checkbox checked={checked} tabIndex={-1} disableRipple sx={{ p: 0.5 }} />
                      <InitialsAvatar
                        src={user.photoURL}
                        name={user.displayName || user.email}
                        size={40}
                      />
                      <ListItemText
                        primary={user.displayName || user.email || user.uid}
                        secondary={secondary || undefined}
                        primaryTypographyProps={{ fontFamily: font(), fontWeight: 600, fontSize: 15 }}
                        secondaryTypographyProps={{ fontFamily: font(), fontSize: 12 }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button fullWidth variant="outlined" onClick={onClose} sx={{ fontFamily: font(), textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirm}
            sx={{
              fontFamily: font(),
              textTransform: 'none',
              bgcolor: '#7D7AFF',
              '&:hover': { bgcolor: '#6B68E5' },
            }}
          >
            Done ({selectedCount})
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
